import { Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import LoadingFallback from "./components/LoadingFallback";
import { lazyWithRetry as lazy } from "./utils/lazyWithRetry";

// Lazy-loaded pages (loaded only when route is visited)
const TermsPage = lazy(() => import("./pages/Policy/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/Policy/PrivacyPage"));
const Accelerator = lazy(() => import("./pages/SelfPaced/Accelerator"));
const EngAccelerator = lazy(() => import("./pages/SelfPaced/EngAccelerator"));
const Tools = lazy(() => import("./pages/SelfPaced/Tools"));
const MasterMind = lazy(() => import("./pages/Workshops/MasterMind"));
const ThreeDayBootcamp = lazy(() => import("./pages/Workshops/ThreeDayBootcamp"));
const EnginnerWorkshop = lazy(
  () => import("./pages/Workshops/EnginnerWorkshop"),
);
const Bootcamp = lazy(() => import("./pages/Workshops/Bootcamp"));
const RefundPage = lazy(() => import("./pages/Policy/RefundPage"));
const Generalist = lazy(() => import("./pages/Fellowship/Generalist"));
const Engineer = lazy(() => import("./pages/Fellowship/Engineer"));
const Alumni = lazy(() => import("./pages/Resource/AlumniPage"));
const Partnerships = lazy(() => import("./pages/Resource/Partnerships"));
const ContactPage = lazy(() => import("./pages/Policy/ContactPage"));
const EngineerWorkshopNew = lazy(() => import("./pages/EngineerWorkshopNew"));
const EngineerWorkshopV3 = lazy(() => import("./pages/EngineerWorkshopV3"));
const Certificate = lazy(() => import("./pages/New/Certificate"));
const Home = lazy(() => import("./pages/New/HomePage"));
const Referral = lazy(() => import("./pages/Resource/ReferralPage"));
const Cohort = lazy(() => import("./pages/Resource/CohortPage"));
const AccReferral = lazy(() => import("./pages/Resource/AccReferral"));
const GeneralistInfl = lazy(
  () => import("./pages/Fellowship/Influencer/GeneralistInfl"),
);
const Credcertificate = lazy(() => import("./pages/New/Credcertificate"));
const MasterMindRef = lazy(() => import("./pages/Workshops/MasterMindRef"));
const MasterMindB2b = lazy(() => import("./pages/Workshops/MasterMindB2b"));
const Certificateeng = lazy(() => import("./pages/New/Certificateeng"));
const AI = lazy(() => import("./pages/Resource/AI"));
const Hotstar = lazy(() => import("./pages/Hotstar/Hotstar"));
const BusinessPage = lazy(() => import("./pages/New/BusinessPage"));

import { useDispatch } from "react-redux";
import { initAnalytics } from "./utils/analytics";
import { useUTMPreservation } from "./hooks/useUTMPreservation";
import { detectCountry } from "./store/slices/countrySlice";
import CurrencyProvider from "./context/CurrencyProvider";
import { HelmetProvider } from "react-helmet-async";
import B2Bpage from "./pages/New/B2Bpage";
import WisprFlow from "./pages/WisprFlow";

// ✅ Additional lazy-loaded pages
const EngineerFellowship = lazy(
  () => import("./pages/Fellowship/EngineerFellowship"),
);
const GeneralistFellowship = lazy(
  () => import("./pages/Fellowship/GeneralistFellowship"),
);

// ✅ Main App Component
const App = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    // Defer ALL non-critical operations significantly to remove from critical path
    // Critical path is typically 0-1000ms, so we delay beyond that

    // Defer analytics initialization significantly to avoid critical path
    // Wait for page to be fully loaded and idle before initializing
    const loadAnalytics = () => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(
          () => {
            setTimeout(() => {
              initAnalytics();
            }, 2000); // Delay 2 seconds after idle to ensure out of critical path
          },
          { timeout: 3000 },
        );
      } else {
        // Fallback: delay significantly after mount
        setTimeout(() => {
          initAnalytics();
        }, 3000);
      }
    };

    // Wait for page to be fully loaded before scheduling analytics
    if (document.readyState === "complete") {
      loadAnalytics();
    } else {
      window.addEventListener("load", loadAnalytics, { once: true });
    }

    // Defer country detection significantly to avoid critical path
    // Delay significantly to ensure ipapi.co call doesn't block critical path
    const loadCountryDetection = () => {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(
          () => {
            setTimeout(() => {
              dispatch(detectCountry());
            }, 4000); // Delay 4 seconds after idle to ensure out of critical path
          },
          { timeout: 5000 },
        );
      } else {
        // Fallback: delay significantly after mount
        setTimeout(() => {
          dispatch(detectCountry());
        }, 5000);
      }
    };

    // Wait for page to be fully loaded + additional delay before scheduling country detection
    if (document.readyState === "complete") {
      setTimeout(loadCountryDetection, 1000); // Additional 1 second delay
    } else {
      window.addEventListener(
        "load",
        () => {
          setTimeout(loadCountryDetection, 1000); // Additional 1 second delay after load
        },
        { once: true },
      );
    }
  }, [dispatch]);

  // Preserve UTM parameters across all internal navigation
  useUTMPreservation();

  return (
    <HelmetProvider>
      <CurrencyProvider>
        <Router>
          <Suspense fallback={<LoadingFallback />}>
            <main id="main-content">
              <Routes>
                <Route path="/terms-and-conditions" element={<TermsPage />} />
                <Route path="/privacy-policy" element={<PrivacyPage />} />
                <Route path="/refund-policy" element={<RefundPage />} />
                <Route path="/contact-us" element={<ContactPage />} />

                <Route path="/mastermind-ind" element={<MasterMind />} />
                <Route path="/mastermind-usd" element={<MasterMind />} />
                <Route path="/mastermind-dark-uk" element={<MasterMind />} />

                <Route
                  path="/engineer-workshop-v2"
                  element={<EngineerWorkshopNew />}
                />
                <Route
                  path="/engineer-workshop-v3"
                  element={<EngineerWorkshopV3 />}
                />
                <Route
                  path="/engineer-workshop-v4"
                  element={<EngineerWorkshopV3 />}
                />
                <Route
                  path="/engineer-workshop-v5"
                  element={<EngineerWorkshopV3 />}
                />
                <Route
                  path="/engineer-workshop-gpay"
                  element={<EngineerWorkshopV3 />}
                />
                <Route
                  path="/engineer-workshop-zomato"
                  element={<EngineerWorkshopV3 />}
                />
                <Route
                  path="/engineer-workshop-swiggy"
                  element={<EngineerWorkshopV3 />}
                />
                <Route
                  path="/engineer-workshop-zepto"
                  element={<EngineerWorkshopV3 />}
                />
                <Route
                  path="/engineer-workshop-blinkit"
                  element={<EngineerWorkshopV3 />}
                />
                <Route
                  path="/engineer-workshop-usa"
                  element={<EngineerWorkshopV3 />}
                />
                <Route
                  path="/engineer-workshop-uk"
                  element={<EngineerWorkshopV3 />}
                />
                <Route
                  path="/engineer-workshop-quora"
                  element={<EngineerWorkshopV3 />}
                />

                {/* NEW */}
                <Route path="/" element={<Home />} />
                <Route
                  path="/6-month-ai-generalist-fellowship"
                  element={<Generalist />}
                />
                <Route path="/6-month-ai-engineering" element={<Engineer />} />
                <Route
                  path="/6-month-ai-engineering-fellowship"
                  element={<EngineerFellowship />}
                />
                <Route
                  path="/6-month-ai-generalist-fellowship-v2"
                  element={<GeneralistFellowship />}
                />
                <Route path="/certificate" element={<Certificate />} />
                <Route
                  path="/moneycontrol-certificate"
                  element={<Certificate />}
                />
                <Route path="/ai-generalist-bootcamp" element={<Bootcamp />} />
                <Route path="/ai-accelerator" element={<Accelerator />} />
                <Route
                  path="/engineering-accelerator"
                  element={<EngAccelerator />}
                />
                <Route path="/ai-tools-deepdive" element={<Tools />} />
                <Route
                  path="/ai-generalist-review-alumni"
                  element={<Alumni />}
                />
                <Route path="/aigf-referral" element={<Referral />} />
                <Route path="/acc-referral" element={<AccReferral />} />
                <Route path="/cohort" element={<Cohort />} />

                <Route path="/2-day-ai-mastermind" element={<MasterMind />} />
                <Route path="/3-day-generative-ai-bootcamp" element={<ThreeDayBootcamp />} />
                <Route
                  path="/2-day-ai-engineering-mastermind"
                  element={<EnginnerWorkshop />}
                />
                <Route
                  path="/6-month-ai-generalist-fellowship-infl-v3"
                  element={<GeneralistInfl />}
                />
                <Route path="/cred-certificate" element={<Credcertificate />} />
                <Route
                  path="/mastermind-referral"
                  element={<MasterMindRef />}
                />
                <Route
                  path="/mastermind-referral-v2-utk"
                  element={<MasterMindB2b />}
                />
                <Route
                  path="/creator-partnerships"
                  element={<Partnerships />}
                />
                <Route path="/certificate-eng" element={<Certificateeng />} />
                <Route path="/ai" element={<AI />} />

                {/* Hotstar Campaign Page */}
                <Route path="/cricket" element={<Hotstar />} />

                <Route path="/business" element={<BusinessPage />} />
                <Route path="/b2b" element={<B2Bpage />} />
                <Route path="/wisprflow" element={<WisprFlow />} />
              </Routes>
            </main>
          </Suspense>
        </Router>
      </CurrencyProvider>
    </HelmetProvider>
  );
};

export default App;
